import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const notificationApi = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout - fail fast instead of hanging
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth token from localStorage on every request
notificationApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally
notificationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('streamToken');
      localStorage.removeItem('streamUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class NotificationService {
  // Get user notifications
  static async getNotifications(limit = 50, offset = 0) {
    try {
      const response = await notificationApi.get(`/notifications?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  // Get unread count
  static async getUnreadCount() {
    try {
      const response = await notificationApi.get('/notifications/unread-count');
      return response.data;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  // Mark notification as read
  static async markAsRead(notificationId) {
    try {
      const response = await notificationApi.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Mark all notifications as read
  static async markAllAsRead() {
    try {
      const response = await notificationApi.put('/notifications/read-all');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // Get admin notifications
  static async getAdminNotifications(limit = 50, offset = 0) {
    try {
      const response = await notificationApi.get(`/notifications/admin/all?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
      throw error;
    }
  }

  // Get notification history (admin sees all, users see their own)
  static async getNotificationHistory(limit = 100, offset = 0, type = 'all') {
    try {
      const response = await notificationApi.get(`/notifications/history?limit=${limit}&offset=${offset}&type=${type}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification history:', error);
      throw error;
    }
  }

  // Get notification statistics (admin only)
  static async getNotificationStats() {
    try {
      const response = await notificationApi.get('/notifications/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }
}

export default NotificationService;
